<script lang="ts" setup>
import { computed, onBeforeMount, ref } from "vue";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import BgWaveCard from "@/components/organisms/common/BgWaveCard.vue";
import RankStar from "@/assets/svg/rank/RankStar.svg?component";
import RankStar32 from "@/assets/svg/rank/RankStar@32.svg?component";
import RankGold from "@/assets/svg/rank/RankGold.svg?component";
import RankGold32 from "@/assets/svg/rank/RankGold@32.svg?component";
import RankSilver from "@/assets/svg/rank/RankSilver.svg?component";
import RankSilver32 from "@/assets/svg/rank/RankSilver@32.svg?component";
import RankBronze from "@/assets/svg/rank/RankBronze.svg?component";
import RankBronze32 from "@/assets/svg/rank/RankBronze@32.svg?component";
import NoRank from "@/assets/svg/rank/NoRank.svg?component";
import NoRank32 from "@/assets/svg/rank/NoRank@32.svg?component";
import BagBadgeBlue from "@/assets/svg/BagBadgeBlue.svg?component";
import StarBadgeYellow from "@/assets/svg/StarBadgeYellow.svg?component";
import rankMap from "@/models/rank";
import { rankService } from "@/app/services/RankService";
import { accountService } from "@/app/services/AccountService";
import { orderService } from "@/app/services/OrderService";
import { evaluationRound } from "@/utils/evaluationRound";
import { authStore } from "@/store/auth";
import {
  addMonths,
  endOfMonth,
  getMonthIndex,
  getYear,
  startOfMonth,
  formatFromUnixTimestamp,
  startOfToday
} from "@/utils/date";
import { useLoader } from "@/utils/loader";
import { useErrorModal } from "@/utils/modal";
import { useCustomRouter } from "@/utils/router";

type Rank = {
  id: number;
  createdAt: string;
  updatedAt: string;
  yearmonth: string;
  rank: number;
  user: string;
};

const loader = useLoader();
const errorModal = useErrorModal();
const customRouter = useCustomRouter();

// 年月ごとのランク
const workerRankList = ref<Record<string, Rank>>({});
const avgEvaluation = ref(0);

const today = computed(() => startOfToday());
const currentYear = computed(() => getYear(today.value));
// 表示用なのでindex + 1
const currentMonth = computed(() => getMonthIndex(today.value) + 1);
const user = computed(() => authStore.user!);
const currentRank = computed(() => user.value.rank);
const currentRankLabel = computed(() => {
  const rankObj = rankMap.find(rank => {
    return rank.id === currentRank.value;
  });
  if (rankObj) {
    return rankObj.name;
  } else {
    return "レギュラー";
  }
});
const nextJudgementPeriod = computed(() => {
  const startDate = startOfMonth(addMonths(today.value, -2));
  const endDate = endOfMonth(addMonths(startDate, 2));
  return `${formatFromUnixTimestamp(
    startDate,
    "MM/dd"
  )}〜${formatFromUnixTimestamp(endDate, "MM/dd")}`;
});
const workCount = computed(() => user.value.workCount);

onBeforeMount(async () => {
  try {
    loader.show();

    // アカウント情報を更新
    await accountService.updateUserData();

    await Promise.all([fetchRankList(), fetchAvgEvaluation()]);
  } catch (error) {
    await errorModal.showErrorModal(error as Error);
  } finally {
    loader.hide();
  }
});

async function onClickBack() {
  await customRouter.pushIgnoreError({ name: "Mypage" });
}
function getRank(month: number) {
  const ym = String(currentYear.value) + String(month).padStart(2, "0");
  const workerRank = workerRankList.value[ym];
  if (workerRank) {
    return workerRank.rank;
  } else {
    return 0;
  }
}
function getCalendarMonth(month: number, i: number) {
  if (i === 1) return month;
  return month + 6;
}
async function fetchRankList() {
  // ランクデータを年月をキーにしたオブジェクトに保存
  const response = await rankService.fetchRankList();
  response.results.forEach(rank => {
    workerRankList.value[rank.yearmonth] = rank;
  });
}
async function fetchAvgEvaluation() {
  const result = await orderService.fetchTotalEvaluationByOffice(user.value.id);
  if (result && result.totalAverage) {
    avgEvaluation.value = result.totalAverage;
  }
}
</script>

<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="onClickBack" />
      </template>
      ランク
    </Header>

    <div class="rank p-3">
      <BgWaveCard class="section1">
        <div class="pb-3 border-bottom border-outline-color">
          <p class="text-mild text-size-14 text-weight-500">
            {{ currentMonth }}月のランク
          </p>
          <div class="d-flex align-items-center">
            <RankStar32 v-if="currentRank === 4" class="mr-2 flex-shrink-0" />
            <RankGold32
              v-else-if="currentRank === 3"
              class="mr-2 flex-shrink-0"
            />
            <RankSilver32
              v-else-if="currentRank === 2"
              class="mr-2 flex-shrink-0"
            />
            <RankBronze32
              v-else-if="currentRank === 1"
              class="mr-2 flex-shrink-0"
            />
            <NoRank32 v-else class="mr-2 flex-shrink-0" />
            <h2 class="rank-label">
              {{ currentRankLabel }}
            </h2>
          </div>
        </div>
        <div class="pt-3">
          <p class="text-mild text-size-14 text-weight-500">
            {{ currentYear }}年
          </p>
          <ul
            v-for="i in 2"
            :key="'performance-list' + i"
            class="performance-list d-flex justify-content-between"
          >
            <li
              v-for="month in 6"
              :key="'performance-item' + month"
              class="text-center"
            >
              <template v-if="getRank(getCalendarMonth(month, i)) === 4">
                <RankStar></RankStar>
              </template>
              <template v-else-if="getRank(getCalendarMonth(month, i)) === 3">
                <RankGold></RankGold>
              </template>
              <template v-else-if="getRank(getCalendarMonth(month, i)) === 2">
                <RankSilver></RankSilver>
              </template>
              <template v-else-if="getRank(getCalendarMonth(month, i)) === 1">
                <RankBronze></RankBronze>
              </template>
              <template v-else>
                <NoRank></NoRank>
              </template>
              <p class="text-size-10">{{ getCalendarMonth(month, i) }}月</p>
            </li>
          </ul>
        </div>
      </BgWaveCard>

      <div class="section2 mt-3">
        <p class="text-mild text-size-14 text-weight-500">本日までの実績</p>
        <div class="d-flex justify-content-between mt-2">
          <div
            class="section2__card flex-grow-1 d-flex align-items-center bg-white border border-outline-color mr-2 round-8"
          >
            <BagBadgeBlue class="left"></BagBadgeBlue>
            <div class="right">
              <p class="text-mild text-size-12">勤務回数</p>
              <p class="text-size-14 text-weight-600">{{ workCount }}回</p>
            </div>
          </div>
          <div
            class="section2__card flex-grow-1 d-flex align-items-center bg-white border border-outline-color round-8"
          >
            <StarBadgeYellow class="left"></StarBadgeYellow>
            <div class="right">
              <p class="text-mild text-size-12">平均評価</p>
              <p
                class="text-size-14 text-weight-600"
                data-testid="avg-evaluation"
              >
                {{ evaluationRound(avgEvaluation) }}点
              </p>
            </div>
          </div>
        </div>
        <p class="text-gray-05 text-size-12 mt-2">
          ＊ 次回更新判定期間は{{ nextJudgementPeriod }}
        </p>
      </div>

      <div class="section3 mt-3">
        <p class="sub-title text-mild text-size-14 text-weight-500">
          ランク
        </p>
        <table class="bg-white round-4 w-100">
          <thead>
            <tr>
              <th class="text-mild text-size-14 text-weight-500 text-center">
                ランク
              </th>
              <th class="text-mild text-size-14 text-weight-500 text-center">
                条件
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="text-center">
                <RankStar32></RankStar32>
                <p class="text-gold text-size-12 text-weight-600">スター</p>
              </td>
              <td>
                <p class="text-size-14">
                  過去3ヶ月で45回以上勤務、かつ平均評価が4.0以上
                </p>
              </td>
            </tr>
            <tr>
              <td class="text-center">
                <RankGold32></RankGold32>
                <p class="text-gold text-size-12 text-weight-600">マスター</p>
              </td>
              <td>
                <p class="text-size-14">
                  過去3ヶ月で30回以上勤務、かつ平均評価が4.0以上
                </p>
              </td>
            </tr>
            <tr>
              <td class="text-center">
                <RankSilver32></RankSilver32>
                <p class="text-silver text-size-12 text-weight-600">プロ</p>
              </td>
              <td>
                <p class="text-size-14">
                  過去3ヶ月で10回以上勤務、かつ平均評価が3.5以上
                </p>
              </td>
            </tr>
            <tr>
              <td class="text-center">
                <RankBronze32></RankBronze32>
                <p class="text-bronze text-size-12 text-weight-600">
                  レギュラー
                </p>
              </td>
              <td>
                <p class="text-size-14">ー</p>
              </td>
            </tr>
          </tbody>
        </table>
        <p class="note text-gray-05 text-size-12" data-testid="caution-rank">
          ＊毎月末日の夜時点での過去3ヶ月間の勤務回数及び評価によって、翌月1日に更新されます。<br />
          ＊平均評価には、お互いの評価が完了した後の事業所側の評価のみ含まれます。
        </p>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.rank {
  .section1 {
    .performance-list {
      padding-top: 6px;

      li {
        p {
          padding-top: 1px;
        }
      }
    }
  }

  .section2 {
    &__card {
      padding: 12px 16px;

      .right {
        margin-left: 10px;
      }
    }
  }

  .section3 {
    .sub-title {
      margin-bottom: 6px;
    }

    .note {
      margin: 6px 0 24px;
    }
  }
}

.rank-label {
  margin-right: -1px;
  font-size: 32px;
  font-weight: 600;
  letter-spacing: -2px;
}

table {
  border-collapse: separate;
  border: 1px solid $outline-color;

  tr {
    th {
      line-height: 1;
    }

    th,
    td {
      padding: 12px;
      border-right: 1px solid $outline-color;
      border-bottom: 1px solid $outline-color;
    }

    th:first-child,
    td:first-child {
      width: 74px;
      padding: 12px 4px;
    }

    th:last-child,
    td:last-child {
      border-right-style: none;
    }

    &:last-child {
      td {
        border-bottom-style: none;
      }
    }
  }
}
</style>
